import { useCallback } from "react";
import useAppDispatch from "../../../hooks/useAppDispatch";
import {
  SetCurrentStep,
  SetProposalLoading,
  UpdateProposalInfoField,
} from "..";
import useAppSelector from "../../../hooks/useAppSelector";
import { MAX_POS_NAME_LENGTH, ProposalInformationType } from "../types";
import { errorLog } from "../../../../utils/debugLog";
import { useGetPointOfSalesLazyQuery } from "../../../../gql/hooks";

const useProposalInformation = () => {
  // Hooks
  const dispatch = useAppDispatch();
  const [getPosInfo] = useGetPointOfSalesLazyQuery();

  // Selectors
  const proposalInfo = useAppSelector(
    (state) => state.proposal.proposalInformation,
  );

  // Proposal Info save
  const onInfoChange = useCallback(
    <K extends keyof ProposalInformationType>(
      key: K,
      value: ProposalInformationType[K],
    ) =>
      dispatch(
        UpdateProposalInfoField({
          key,
          value,
        }),
      ),
    [dispatch],
  );

  // Proposal Setter
  const onPointOfSaleId = useCallback(
    async (id?: number) => {
      dispatch(SetProposalLoading(true));
      try {
        if (id !== undefined) {
          dispatch(
            UpdateProposalInfoField({
              key: "posId",
              value: id,
            }),
          );

          const { data: posData } = await getPosInfo({
            variables: { id },
          });

          const retrievedData = posData?.pointOfSales.find(
            ({ id: itemId }) => itemId === id,
          );

          if (retrievedData) {
            const name = `${retrievedData.name.slice(
              0,
              MAX_POS_NAME_LENGTH - 11,
            )}_${new Date().toISOString().slice(0, 10)}`;

            dispatch(
              UpdateProposalInfoField({
                key: "name",
                value: name,
              }),
            );
            dispatch(
              UpdateProposalInfoField({
                key: "pos",
                value: retrievedData,
              }),
            );
          } else {
            console.warn("Pos not found", retrievedData, id, posData);
          }
        }
        dispatch(SetProposalLoading(false));
      } catch (err) {
        dispatch(SetProposalLoading(false));
        errorLog(err, "Pos Error");
      }
    },
    [dispatch, getPosInfo],
  );

  const onCurrentStepUpdate = useCallback(
    (value: number) => dispatch(SetCurrentStep(value)),
    [dispatch],
  );

  return {
    proposalInfo,
    onInfoChange,
    onPointOfSaleId,
    onCurrentStepUpdate,
  };
};

export default useProposalInformation;
